import axios from 'axios';

// Get CSRF token from meta tag
const getCSRFToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
};

// Create axios instance with default config
const apiClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add CSRF token to all requests
apiClient.interceptors.request.use((config) => {
  config.headers['X-CSRF-Token'] = getCSRFToken();
  return config;
});

/**
 * Set flash message to be shown after redirect
 * Flash messages are stored in sessionStorage and displayed by Rails
 * @param {string} message - The flash notice message
 */
const setFlashNotice = (message) => {
  // Store in sessionStorage to be read by Rails after redirect
  sessionStorage.setItem('flash_notice', message);
};

/**
 * Save daily target for a child
 * @param {number} childId - Child ID
 * @param {Object} targetData - Target data
 * @param {string} targetData.mode - 'basic' or 'advanced'
 * @param {number} targetData.targetXp - Basic mode XP target
 * @param {Object} targetData.weekdayXp - Advanced mode XP per day
 * @param {boolean} isUpdate - Whether this is an update (PATCH) or create (POST)
 * @returns {Promise} API response
 */
export const saveDailyTarget = async (childId, targetData, isUpdate = false) => {
  const payload = {
    child_daily_target: {
      mode: targetData.mode,
      target_xp: targetData.targetXp,
      monday_xp: targetData.weekdayXp.mondayXp,
      tuesday_xp: targetData.weekdayXp.tuesdayXp,
      wednesday_xp: targetData.weekdayXp.wednesdayXp,
      thursday_xp: targetData.weekdayXp.thursdayXp,
      friday_xp: targetData.weekdayXp.fridayXp,
      saturday_xp: targetData.weekdayXp.saturdayXp,
      sunday_xp: targetData.weekdayXp.sundayXp
    }
  };

  const url = `/children/${childId}/daily_target`;
  const method = isUpdate ? 'patch' : 'post';

  const response = await apiClient[method](url, payload);

  // Store flash message for display after redirect
  if (response.data.notice) {
    setFlashNotice(response.data.notice);
  }

  return response;
};

/**
 * Apply daily target to all children in the family
 * @param {number} childId - Source child ID
 * @param {Object} targetData - Target data to apply
 * @returns {Promise} API response
 */
export const applyToAllChildren = async (childId, targetData) => {
  const payload = {
    child_daily_target: {
      mode: targetData.mode,
      target_xp: targetData.targetXp,
      monday_xp: targetData.weekdayXp.mondayXp,
      tuesday_xp: targetData.weekdayXp.tuesdayXp,
      wednesday_xp: targetData.weekdayXp.wednesdayXp,
      thursday_xp: targetData.weekdayXp.thursdayXp,
      friday_xp: targetData.weekdayXp.fridayXp,
      saturday_xp: targetData.weekdayXp.saturdayXp,
      sunday_xp: targetData.weekdayXp.sundayXp
    }
  };

  const response = await apiClient.post(`/children/${childId}/daily_target/apply_to_all`, payload);

  // Store flash message for display after redirect
  if (response.data.notice) {
    setFlashNotice(response.data.notice);
  }

  return response;
};
